import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faTag } from '@fortawesome/pro-solid-svg-icons/faTag';
import { faCalendarDays } from '@fortawesome/pro-solid-svg-icons/faCalendarDays';
import { faChild } from '@fortawesome/pro-solid-svg-icons/faChild';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faClock } from '@fortawesome/pro-solid-svg-icons/faClock';
import { faDollarSign } from '@fortawesome/pro-solid-svg-icons/faDollarSign';
import { faHouseHeart } from '@fortawesome/pro-solid-svg-icons/faHouseHeart';
import { faIdCard } from '@fortawesome/pro-solid-svg-icons/faIdCard';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons/faLocationDot';
import { faMapLocationDot } from '@fortawesome/pro-solid-svg-icons/faMapLocationDot';

import { Facility } from 'us-web-services';
import '../i18n/config';
import { StarRating } from '../Rating/StarRating';
import { onEnterAction } from '../utils/a11y';
import { decodeHtmlText } from '../utils/string';
import { getRandomFacilityDefaultImageUrl } from '../constants/FacilityDefaultImages';

export interface FacilityProps {
  facility: Facility;
  showDescription?: boolean;
  trackingCallback?: (action: string, props: Record<string, unknown>) => void;
  onFacilitySelect?: (facilityUrl: string) => void;
};

interface IconLineProps {
  icon: IconProp;
  iconClass: string;
  text: string | JSX.Element;
  clamp?: boolean;
  containerClass?: string;
  large?: boolean;
  titleText?: string;
  color?: string;
  onClick?: React.MouseEventHandler;
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
}

const formatDistance = (distance: number): string => {
  if (distance < 1) {
    return '< 1 mile';
  }

  return `${Math.round(distance * 10) / 10} miles`;
};

const IconLine = ({
  icon,
  iconClass,
  text,
  clamp = false,
  containerClass = 'mt-1',
  large = false,
  titleText = '',
  color = '',
  onClick,
  onKeyDown,
}: IconLineProps): JSX.Element => {
  const interactiveProps = onClick ? {
    onClick,
    onKeyDown,
    role: 'button' as const,
    tabIndex: 0 as const,
  } : {};

  return (
    <div className='flex' {...interactiveProps}>
      <div className={`flex${containerClass ? ` ${containerClass}` : ''}`}>
        <div className='text-center w-4 mr-2 content-center'>
          <FontAwesomeIcon title={titleText} titleId={titleText} aria-labelledby={titleText} icon={icon} className={iconClass} />
        </div>
        <p className={`${large ? 'text-lg' : 'text-sm'}${clamp ? ' line-clamp-2 leading-tight' : ''}${color ? ` ${color}` : ''}`}>{text}</p>
      </div>
    </div>
  );
};

const LICENSE_EXEMPT_STATES = ['AL', 'CT', 'FL', 'ID', 'IA', 'IN', 'MN', 'MO', 'NH', 'VA'];

export const FacilitySearchCard = ({ facility, showDescription, trackingCallback, onFacilitySelect }: FacilityProps): JSX.Element => {
  const { t } = useTranslation();

  const isDescriptionTruncated = facility.profile_description_teaser && facility.profile_description_teaser.length > 100;

  const getImageSrc = (): string => facility.title_image || getRandomFacilityDefaultImageUrl();

  const hasOpenSpots = facility.total_open_spots_count > 0;

  const showRating = !facility.hide_reviews && !!facility.has_any_reviews
    && !!facility.average_review_score && !!facility.review_count;
  const ratingValue = showRating ? facility.average_review_score! * 20 : 0;

  const handleClick = async (): Promise<void> => {
    const trackingProps = {
      elementId: 'facility-search-card',
      elementLink: facility.friendly_id,
      elementText: 'Learn More',
    };

    if (trackingCallback) {
      trackingCallback('clicked-button', trackingProps);
    }

    onFacilitySelect?.(facility.friendly_id);
  };

  const handleRatingClick = async (): Promise<void> => {
    const trackingProps = {
      elementId: 'facility-search-card-rating',
      elementLink: `${facility.friendly_id}#reviews-section`,
      elementText: 'Rating',
    };

    if (trackingCallback) {
      trackingCallback('clicked-rating', trackingProps);
    }

    onFacilitySelect?.(`${facility.friendly_id}?redirect_fragment=reviews-section`);
  };

  return (
    <div
      aria-label={t('components.facilitySearchCard.ariaLabel', { facilityName: facility.name })}
      className={'bg-white rounded-2xl h-fit cursor-pointer hover:bg-gray-100 lg:max-w-[410px] text-left ' +
        `${hasOpenSpots ? 'mt-[3px]' : 'mt-[29px]'}`} // offset margins for conditional IconLine so all cards line up vertically
      onClick={(e): void => { e.stopPropagation(); handleClick(); }}
      onKeyDown={(e): void => { e.stopPropagation(); onEnterAction(e, handleClick); }}
      role='button'
      tabIndex={0}
    >
        { hasOpenSpots &&
        <IconLine
          containerClass='mt-[-9px] pt-[8px] pl-[2px]'
          icon={faCircleCheck}
          iconClass='text-honey'
          large
          text={t('components.facilitySearchCard.openSpots')}
          titleText={t('components.facilitySearchCard.openSpots')}
        />}

        <div className='h-full grid contents'>
          <img
            alt={facility.name}
            className='cursor-pointer w-full h-[180px] mb-2 object-cover rounded-[10px]'
            src={getImageSrc()}
          />
          <p className='text-xl px-1 font-semibold text-left leading-tight line-clamp-2'>{decodeHtmlText(facility.name)}</p>
          <div className='p-2'>
            { facility.offers && facility.offers.length > 0 &&
            <IconLine
              icon={faTag}
              iconClass='text-bubblegum'
              text={facility.offers.join(', ')}
              clamp
              containerClass='mb-2 font-semibold'
              titleText={t('components.facilitySearchCard.specialOffer')}
              color='text-emerald-700'
            />}

            { showDescription && facility.profile_description_teaser &&
            <div className='mb-2'>
              <p className='text-lg leading-tight line-clamp-3'>
                {decodeHtmlText(facility.profile_description_teaser)}
              </p>
              {isDescriptionTruncated &&
              <button
                className='text-sapphire underline'
                onClick={(e): void => { e.stopPropagation(); handleClick(); }}
                onKeyDown={(e): void => { e.stopPropagation(); onEnterAction(e, handleClick); }}
                type='button'
              >
                {t('components.facilitySearchCard.readMore')}
              </button>}
            </div>}

            {showRating &&
            <StarRating
              rating={ratingValue}
              reviewCount={facility.review_count ?? 0}
              className='mt-1'
              onClick={handleRatingClick}
              ariaLabel={t('components.facilitySearchCard.rating')}
            />}

            <IconLine
              icon={faLocationDot}
              iconClass='text-sapphire-300'
              text={`${facility.city}, ${facility.state} ${facility.zip}`}
              titleText={t('components.facilitySearchCard.location')}
            />

            {facility.distance != null &&
            <IconLine
              icon={faMapLocationDot}
              iconClass='text-sapphire-300'
              text={`${formatDistance(facility.distance)}`}
              titleText={t('components.facilitySearchCard.distance')}
            />}

            {facility.display_age_group_string &&
            <IconLine
              icon={faChild}
              iconClass='text-sapphire-300'
              text={facility.display_age_group_string}
              titleText={t('components.facilitySearchCard.ageRanges')}
            />}

            {facility.display_schedule_string &&
            <IconLine
              icon={faClock}
              iconClass='text-sapphire-300'
              text={facility.display_schedule_string}
              titleText={t('components.facilitySearchCard.schedule')}
            />}

            {facility.schedule_types.length > 0 &&
            <IconLine
              icon={faCalendarDays}
              iconClass='text-sapphire-300'
              text={facility.schedule_types.join(', ')}
              titleText={t('components.facilitySearchCard.careTypes')}
              clamp
            />}

            {facility.display_tuition_string && !!facility.price_from && !!facility.price_to &&
            <IconLine
              icon={faDollarSign}
              iconClass='text-sapphire-300'
              text={`$${facility.display_tuition_string}`}
              titleText={t('components.facilitySearchCard.tuition')}
            />}

            {facility.class_and_philosophies &&
            <IconLine
              icon={faHouseHeart}
              iconClass='text-sapphire-300'
              text={facility.class_and_philosophies}
              clamp={facility.class_and_philosophies.length > 22}
              titleText={t('components.facilitySearchCard.facilityType')}
            />}

            {facility.licenses && facility.licenses.length > 0 &&
            <IconLine
              icon={faIdCard}
              iconClass='text-sapphire-300'
              clamp
              text={<>{facility.state} {t(`components.facilitySearchCard.${LICENSE_EXEMPT_STATES.includes(facility.state) ? 'registration' : 'license'}`)}
                {facility.licenses.map((license, index) => (
                <span key={license.number}>
                  {(license.link || facility.state_license_url) ?
                    <a
                      className='text-sapphire underline'
                      href={license.link ?? facility.state_license_url}
                      title={t('components.facilitySearchCard.licenseLinkTitle')}
                      onClick={(e): void => e.stopPropagation() }
                      rel='noreferrer'
                      target='_blank'>
                      &nbsp;#{license.number}
                    </a>
                    : ` #${license.number}`
                  }
                  {index < facility.licenses!.length - 1 && ', '}
                </span>))}
              </>}
              titleText={t('components.facilitySearchCard.licenseInformation')}
            />}
          </div>
        </div>
      </div>
  );
};
